import React, { useState, useEffect } from "react";
import DropdownFilter from "../../components/DropdownFilter/DropdownFilter";
import { useLocation } from "react-router-dom";
import SmallPrint from "../../components/SmallPrint/SmallPrint";
import { EmiData } from "./models/EmiData";
import { getDataPoints } from "../emi";
import queryString from "query-string";
import LineChart from "../../components/LineChart/LineChart";
import { round } from "../../utils/math";

import "./Emi.css";
import Loading from "../../components/Loading/Loading";

type DataState = {
    datapoints: EmiData[];
    categories: string[];
    quarters: string[];
};

type FilterState = {
    category: string | null;
};

type ResultState = {
    datapoints: number[];
    quarters: string[];
};

function Emi() {
    let location = useLocation();
    let locationHash = queryString.parse(location.search);

    // State variable and setter for storing all data in the fetched CSV
    const [data, setData] = useState<DataState>({
        datapoints: [],
        categories: [],
        quarters: []
    });

    // State variable and setter to allow the user to select a specific indicator
    const [filter, setFilter] = useState<FilterState>({
        category: null
    });

    // State variable and setter to store a subset of the data to render
    const [result, setResult] = useState<ResultState>({ datapoints: [], quarters: [] });

    useEffect(() => {
        // Fetch the data from the csv and transform it to the shape we want
        getDataPoints().then(datapoints => {
            const categories = datapoints.map(point => point.indicator);

            setData({
                datapoints,
                quarters: Object.keys(datapoints[0]).filter(key => key !== "indicator" && key !== "sample"),
                categories: categories
            });

            // Let the filter set the first category as default
            setFilter(filter => ({
                ...filter,
                category: categories[0]
            }));
        });
    }, []);

    // On each filter change (check the useEffect dependency array), update the result data used
    // to plot the graph, triggering a re-render.
    useEffect(() => {
        const series = data.datapoints.find(point => point.indicator === filter.category);

        if (series == null) {
            return;
        }

        // Destructure the dataseries and strip out the values
        const { indicator, sample, ...emiValues } = series;

        const result =
            // Transform the raw data from the CSV to a more graph-friendly format            
            Object.entries(emiValues).map(([quarter_key, emiValue]: [string, string]) => ({       
                year: parseInt(quarter_key.split("_")[1]),  // Derive a year to filter by 
                quarter: quarter_key.split("_")[1] + "-" + quarter_key.split("_")[0].toUpperCase(),
                value: round(parseFloat(emiValue.replace(',', '.')), 2),
            }))
            // Filter the data statically to avoid crowding the number of data points            
            .filter(item => item.year >= 2010);

        setResult({
            datapoints: result.map(point => point.value),
            quarters: result.map(point => point.quarter)
        });
    }, [filter, data]);

    if (data.datapoints.length === 0) {
        return <Loading />;
    }

    return (
        <>
            <DropdownFilter
                title="Indicator"
                onChange={item => setFilter({ ...filter, category: item })}
                value={filter.category}
                options={data.categories}
                extractTitle={item => item}
                extractValue={item => item}
                testId="indicator"
            />

            <LineChart
                data={result.datapoints}
                labels={result.quarters}
                xLabel="Quarter"
                yLabel={filter.category ?? ""}
                title={"Business Sweden Export Managers' Index (EMI)"}
                download={locationHash.download === "1"}
                preventRenderingQuarters              
            />

            <SmallPrint>
                <p>* Source: Statistics Sweden, Business Sweden ({new Date().getFullYear()})</p>
            </SmallPrint>
        </>
    );
}

export default Emi;
